import { Button, Modal, Tooltip } from "antd";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "next-i18next";
import styled from "styled-components";
import { getCookie } from "cookies-next";

import { poppins } from "@/components/common/fonts";
import { IconArtGeneratorCredit, IconGenerativeCreditQuestion } from "@/components/common/Icons";
import { useSubscription } from "@/hooks/useSubscription";
import { usePurchasePopup } from "@/components/common/purchasePopup/PurchasePopupContext";
import { PURCHASE_TYPE } from "@/components/common/purchasePopup/constants";

export const CreditsBalancePopup = ({
  isOpened,
  onClose,
  withAddGenerativeCredits,
}: {
  isOpened?: boolean;
  onClose?: () => void;
  withAddGenerativeCredits?: boolean;
}) => {
  const { t } = useTranslation("common");

  const locale = (getCookie("NEXT_LOCALE") as string) || "en";

  const { plan, generativeCredits } = useSubscription();

  /**
   * 차감 예정 크레딧
   */
  const reservedDeductionCredits = generativeCredits?.reservedCredits ?? 0;

  const basicCredits = generativeCredits?.creditDetail.basicCredits ?? 0;
  const addOnCredits = generativeCredits?.creditDetail.addOnCredits ?? 0;

  const planRenewalDate = plan?.servicePeriodEnd;

  const extraCreditDetails = useMemo<
    {
      credits: number;
      expires: Date;
      usedCredits: number;
    }[]
  >(() => {
    return [
      // TODO: 추후 추가 예정
      // { credits: 10, expires: new Date("2025-05-20"), usedCredits: 10 },
      // { credits: 10, expires: new Date("2025-05-21"), usedCredits: 10 },
      // { credits: 10, expires: new Date("2025-05-22"), usedCredits: 0 },
      // { credits: 10, expires: new Date("2025-05-23"), usedCredits: 0 },
      // { credits: 10, expires: new Date("2025-05-24"), usedCredits: 0 },
      // { credits: 10, expires: new Date("2025-05-25"), usedCredits: 0 },
      // { credits: 10, expires: new Date("2025-05-26"), usedCredits: 0 },
    ];
  }, []);

  const formatDate = useMemo(() => {
    const intl = new Intl.DateTimeFormat(locale, {
      dateStyle: "long",
    });

    return intl.format;
  }, [locale]);

  const [now, setNow] = useState<Date>(() => new Date());

  useEffect(() => {
    setNow(new Date());
  }, []);

  const {
    remainingCredits,
    // remainingExtraCredits,
    remainingBasicCredits,
    remainingAddOnCredits,
    // remainingExtraCreditDetails,
  } = useMemo(() => {
    const extraCreditDetailsBeforeRenewalDate = extraCreditDetails
      .filter((extraCredit) => extraCredit.expires < (planRenewalDate ?? now))
      .sort((a, b) => a.expires.getTime() - b.expires.getTime());

    let calculatedRemainingBasicCredits = basicCredits;
    let calculatedAddOnCredits = addOnCredits;

    const extraCreditDetailsAfterRenewalDate = extraCreditDetails
      .filter((extraCredit) => extraCredit.expires > (planRenewalDate ?? now))
      .sort((a, b) => a.expires.getTime() - b.expires.getTime());

    let calculatedReservedDeductionCredits = reservedDeductionCredits;

    const calculate = () => {
      if (calculatedReservedDeductionCredits <= 0) {
        return;
      }

      for (const [
        index,
        extraCredit,
      ] of extraCreditDetailsBeforeRenewalDate.entries()) {
        if (calculatedReservedDeductionCredits <= 0) {
          return;
        }

        const { credits, usedCredits } = extraCredit;

        const canDeductionExtraCredits = credits - usedCredits;

        if (canDeductionExtraCredits <= 0) {
          continue;
        }

        const deductionExtraCredits = Math.min(
          canDeductionExtraCredits,
          calculatedReservedDeductionCredits,
        );

        calculatedReservedDeductionCredits -= deductionExtraCredits;

        extraCreditDetailsBeforeRenewalDate[index] = {
          ...extraCredit,
          usedCredits: extraCredit.usedCredits + deductionExtraCredits,
        };
      }

      if (calculatedReservedDeductionCredits <= 0) {
        return;
      }

      const canDeductionAddOnCredits = Math.min(
        calculatedAddOnCredits,
        calculatedReservedDeductionCredits,
      );

      calculatedReservedDeductionCredits -= canDeductionAddOnCredits;

      calculatedAddOnCredits -= canDeductionAddOnCredits;

      if (calculatedReservedDeductionCredits <= 0) {
        return;
      }

      const canDeductionBasicCredits = Math.min(
        calculatedRemainingBasicCredits,
        calculatedReservedDeductionCredits,
      );

      calculatedReservedDeductionCredits -= canDeductionBasicCredits;

      calculatedRemainingBasicCredits -= canDeductionBasicCredits;

      if (calculatedReservedDeductionCredits <= 0) {
        return;
      }

      for (const [
        index,
        extraCredit,
      ] of extraCreditDetailsAfterRenewalDate.entries()) {
        if (calculatedReservedDeductionCredits <= 0) {
          return;
        }

        const { credits, usedCredits } = extraCredit;

        const canDeductionExtraCredits = credits - usedCredits;

        if (canDeductionExtraCredits <= 0) {
          return;
        }

        const deductionExtraCredits = Math.min(
          canDeductionExtraCredits,
          calculatedReservedDeductionCredits,
        );

        calculatedReservedDeductionCredits -= deductionExtraCredits;

        extraCreditDetailsAfterRenewalDate[index] = {
          ...extraCredit,
          usedCredits: extraCredit.usedCredits + deductionExtraCredits,
        };
      }
    };

    calculate();

    const concatenatedExtraCredits = extraCreditDetailsBeforeRenewalDate
      .concat(extraCreditDetailsAfterRenewalDate)
      .sort((a, b) => b.expires.getTime() - a.expires.getTime());

    const calculatedRemainingExtraCredits = concatenatedExtraCredits.reduce(
      (acc, extraCredit) => {
        return acc + extraCredit.credits - extraCredit.usedCredits;
      },
      0,
    );

    const calculatedRemainingCredits =
      calculatedRemainingBasicCredits +
      calculatedAddOnCredits +
      calculatedRemainingExtraCredits;

    return {
      remainingCredits: calculatedRemainingCredits,
      remainingExtraCredits: calculatedRemainingExtraCredits,
      remainingBasicCredits: calculatedRemainingBasicCredits,
      remainingAddOnCredits: calculatedAddOnCredits,
      remainingExtraCreditDetails: concatenatedExtraCredits,
    };
  }, [
    reservedDeductionCredits,
    basicCredits,
    addOnCredits,
    planRenewalDate,
    extraCreditDetails,
    now,
  ]);

  /**
   * Generative Credits 추가 구매 팝업 열기
   */
  const { open: openPurchasePopup } = usePurchasePopup();
  const handleClickMoreCreditButton = useCallback(() => {
    openPurchasePopup?.({
      type: PURCHASE_TYPE.ADD_ON_GENERATIVE_CREDITS,
    });
    onClose?.();
  }, [onClose, openPurchasePopup]);

  return (
    <StyledModal
      open={isOpened}
      onCancel={onClose}
      width={700}
      centered
      destroyOnClose
      footer={null}
    >
      <Container>
        <Header>
          <Title>{t("Credit Balance")}</Title>
        </Header>

        <Summary>
          <SummaryItem>
            <SummaryItemTitle>{t("Remaining Credits")}</SummaryItemTitle>
            <SummaryItemContent>{remainingCredits}</SummaryItemContent>
          </SummaryItem>
          <Operator>=</Operator>
          <SummaryItem>
            <SummaryItemTitle>
              {t("Plan Basic Credits")}
              <CreditTooltip
                title={t(
                  "Credits included in your plan and renew automatically."
                )}
              />
            </SummaryItemTitle>
            <SummaryItemContent>{remainingBasicCredits}</SummaryItemContent>
          </SummaryItem>
          <Operator>+</Operator>
          <SummaryItem>
            <SummaryItemTitle>
              {t("Add-on Credits")}
              <CreditTooltip
                title={t(
                  "Credits added through add-ons. Renew with your plan."
                )}
              />
            </SummaryItemTitle>
            <SummaryItemContent>{remainingAddOnCredits}</SummaryItemContent>
          </SummaryItem>
          {/* 
            TODO: 엑스트라 크레딧은 추후 추가 예졍
          */}
          {/* <Operator>+</Operator>
          <SummaryItem>
            <SummaryItemTitle>
              {t("Extra Credits")}

              <CreditTooltip
                title={t(
                  "One-time credits that expire 30 days after purchase.",
                )}
              />
            </SummaryItemTitle>
            <SummaryItemContent>{remainingExtraCredits}</SummaryItemContent>
          </SummaryItem> */}
        </Summary>

        <Details>
          <Detail>
            <DetailDescription>
              <DetailDescriptionTitle>
                {t("Plan Basic Credits")}{" "}
                <span>
                  (
                  {plan?.normalized === "free" ? (
                    t("Free Plan")
                  ) : (
                    <>
                      {plan?.normalized === "personal" && t("Personal")}
                      {plan?.normalized === "team" && t("Team")} /{" "}
                      {plan?.interval === "month" && t("Monthly")}
                      {plan?.interval === "year" && t("Yearly")}
                    </>
                  )}
                  )
                </span>
              </DetailDescriptionTitle>
              <DetailDescriptionContent>
                {planRenewalDate
                  ? t("Renews on {{date}}", {
                      date: formatDate(planRenewalDate),
                    })
                  : "-"}
              </DetailDescriptionContent>
            </DetailDescription>
            <DetailCredits>{remainingBasicCredits}</DetailCredits>
          </Detail>

          <Detail>
            <DetailDescription>
              <DetailDescriptionTitle>
                {t("Plan Add-On Credits")}
              </DetailDescriptionTitle>
              <DetailDescriptionContent>
                {planRenewalDate
                  ? t("Renews on {{date}}", {
                      date: formatDate(planRenewalDate),
                    })
                  : "-"}
              </DetailDescriptionContent>
            </DetailDescription>
            <DetailCredits>{remainingAddOnCredits}</DetailCredits>
          </Detail>

          {/* 
            TODO: 엑스트라 크레딧은 추후 추가 예졍
          */}
          {/* {remainingExtraCreditDetails.map((extraCredit, index) => (
            <Detail key={index}>
              <DetailDescription>
                <DetailDescriptionTitle>
                  {t("Extra Credits")}{" "}
                  <span className="expires">
                    ({t("expires 30 days after purchase")})
                  </span>
                </DetailDescriptionTitle>
                <DetailDescriptionContent>
                  {t("Expires on {{date}}", {
                    date: formatDate(extraCredit.expires),
                  })}
                </DetailDescriptionContent>
              </DetailDescription>
              <DetailCredits>
                {extraCredit.credits - extraCredit.usedCredits}
              </DetailCredits>
            </Detail>
          ))} */}

          {/* 
            TODO: 엑스트라 크레딧은 추후 추가 예졍
          */}
          {/* 
            추가 크레딧이 없으면 빈 아이템 하나 보여줌
          */}
          {/* {remainingExtraCreditDetails.length === 0 && (
            <Detail>
              <DetailDescription>
                <DetailDescriptionTitle>
                  {t("Extra Credits")}{" "}
                  <span className="expires">
                    ({t("expires 30 days after purchase")})
                  </span>
                </DetailDescriptionTitle>
                <DetailDescriptionContent>-</DetailDescriptionContent>
              </DetailDescription>
              <DetailCredits>0</DetailCredits>
            </Detail>
          )} */}

          {/*
            withAddGenerativeCredits이 true일 경우 해당 버튼 노출
           */}
          {withAddGenerativeCredits && (
            <Detail>
              <MoreCreditButton
                type="primary"
                onClick={handleClickMoreCreditButton}
              >
                <IconArtGeneratorCredit />
                {t("Get Credits")}
              </MoreCreditButton>
            </Detail>
          )}
        </Details>
      </Container>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  .ant-modal-content {
    padding: 0;
  }
`;

const Container = styled.div`
  padding: 24px 24px 0px 24px;
  max-height: 670px;
  overflow: auto;

  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.div`
  color: #222;
  ${poppins.style}
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 28px; /* 175% */
`;

const Summary = styled.div`
  display: flex;
  justify-content: space-around;
  gap: 16px;

  padding: 20px 10px;
  margin-bottom: 24px;

  border-radius: 4px;
  border: 1px solid #eaeaea;
  background: #fff;

  overflow-x: auto;
`;

const Operator = styled.div`
  display: flex;
  align-items: center;

  color: #999;
  ${poppins.style}
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 40px; /* 250% */
  letter-spacing: -1px;
`;

const SummaryItem = styled.div`
  flex-shrink: 0;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`;

const SummaryItemTitle = styled.div`
  display: flex;

  color: #333;
  ${poppins.style}
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px; /* 138.462% */

  svg {
    color: #999;
  }
`;

const SummaryItemContent = styled.div`
  color: #333;
  ${poppins.style}
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 32px; /* 177.778% */
  letter-spacing: -1px;
`;

const Details = styled.div`
  padding-bottom: 24px;
  overflow-y: auto;

  flex-shrink: 1;
  min-height: 100px;
`;

const Detail = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: 7px 10px 10px;

  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

/**
 * - & .expires
 */
const DetailDescriptionTitle = styled.div`
  color: #333;
  ${poppins.style}
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;

  & span {
    display: inline-block;
  }

  & .expires {
    color: #999;
    font-size: 13px;
    font-style: italic;
    font-weight: 300;
  }
`;

const DetailDescriptionContent = styled.div`
  color: #333;
  ${poppins.style}
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

const DetailCredits = styled.div`
  color: #4276ee;
  ${poppins.style}
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`;

const CreditTooltip = ({ title }: { title?: ReactNode }) => {
  return (
    <StyledTooltip
      title={<TooltipContent>{title}</TooltipContent>}
      mouseEnterDelay={0.5}
      css={`
        cursor: pointer;
      `}
    >
      <IconGenerativeCreditQuestion />
    </StyledTooltip>
  );
};

const StyledTooltip = styled(Tooltip)``;

const TooltipContent = styled.div`
  .ant-tooltip-inner:has(&) {
    padding: 0;
    border-radius: 4px;
  }

  padding: 10px;
  color: #fff;
  ${poppins.style}
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

export const MoreCreditButton = styled(Button)`
  display: flex;
  width: 100%;
  padding: 10px 20px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;

  height: auto;
  border: none;

  border-radius: 8px;
  background: linear-gradient(
      180deg,
      rgba(126, 130, 237, 0.3) -10.15%,
      rgba(73, 197, 228, 0.7) 110.15%
    ),
    linear-gradient(135deg, #7490ff 19.93%, #235fde 50.38%, #4276ee 80.08%);

  color: #fff;
  text-align: center;
  ${poppins.style}
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px; /* 142.857% */

  &&&:hover {
    background: linear-gradient(
        180deg,
        rgba(126, 130, 237, 0.3) -10.15%,
        rgba(73, 197, 228, 0.7) 110.15%
      ),
      linear-gradient(135deg, #7490ff 19.93%, #235fde 50.38%, #4276ee 80.08%);
    opacity: 0.9;
  }

  &&&:active {
    opacity: 0.8;
  }
`;
